import { CommandResponseMode, FrameType, type Xrs2iFrame } from "./types";

const DEVICE_ERROR_PATTERN = /^\(([A-F0-9]{2})\)$/;

/**
 * Parses one raw scanner payload into the frame shape the protocol state machine expects.
 * The parser keeps ACK, error, bracketed data, and plain text separate because each one drives a different branch.
 */
export function parseXrs2iFrame(serializedPayload: string): Xrs2iFrame {
  const raw = `${serializedPayload ?? ""}`.trim();

  if (raw === "^") {
    return { type: FrameType.ACK, raw };
  }

  const errorMatch = DEVICE_ERROR_PATTERN.exec(raw);
  if (errorMatch) {
    return {
      type: FrameType.ERROR,
      code: errorMatch[1],
      raw,
    };
  }

  if (raw.startsWith("[") && raw.endsWith("]")) {
    const value = raw.slice(1, -1).trim();
    return {
      type: FrameType.BRACKET,
      value: value || null,
      raw,
    };
  }

  return {
    type: FrameType.TEXT,
    value: raw,
    raw,
  };
}

/**
 * Checks whether the current frame matches the response style a command was waiting for.
 * This is what lets the command queue release the next command only after the scanner answers the current one.
 */
export function matchesResponseMode(
  responseMode: CommandResponseMode,
  frame: Xrs2iFrame,
): boolean {
  switch (responseMode) {
    case CommandResponseMode.NONE:
      return matchesNoneResponseMode();
    case CommandResponseMode.ACK:
      return matchesAckResponseMode(frame);
    case CommandResponseMode.DATA:
      return matchesDataResponseMode(frame);
    case CommandResponseMode.ACK_OR_DATA:
      return matchesAckOrDataResponseMode(frame);
    default:
      return false;
  }
}

/**
 * Treats scanner error code 14 as retryable busy state.
 * The TruTest device uses this code when it is still working and needs a short resend delay instead of a hard failure.
 */
export function isRetryableDeviceErrorCode(code: string): boolean {
  return code === "14";
}

function matchesNoneResponseMode(): boolean {
  return false;
}

function matchesAckResponseMode(frame: Xrs2iFrame): boolean {
  // ACK-only commands advance only when the scanner confirms receipt with the caret frame.
  return frame.type === FrameType.ACK;
}

function matchesDataResponseMode(frame: Xrs2iFrame): boolean {
  // Data commands accept either bracketed payloads or plain text because the scanner uses both encodings.
  return frame.type === FrameType.BRACKET || frame.type === FrameType.TEXT;
}

function matchesAckOrDataResponseMode(frame: Xrs2iFrame): boolean {
  // Some commands can complete on either an ACK or a payload, so we accept both forms here.
  return (
    frame.type === FrameType.ACK ||
    frame.type === FrameType.BRACKET ||
    frame.type === FrameType.TEXT
  );
}
